import React from "react";
import { Routes, Route, Outlet, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import AdsPage from "./Components/AdsPage/Index";
import Homepage from "./Components/Homepage/Index";
import AdDetails from "./Components/AdDetails/Index";
import Dashboard from "./Components/Dashboard/Sidebar";
import PrivateRoute from "./Components/utils/PrivateRoutes"; // Import PrivateRoute
import PublicRoute from "./Components/utils/PublicRoute"; // Adjust path as needed
import MyAds from "./Components/Dashboard/MyAds/Index";
import EditAd from "./Components/Dashboard/MyAds/EditAd/Index";
import PostAd from "./Components/Dashboard/MyAds/PostAd/Index";
import Profile from "./Components/Dashboard/Profile/Index";
import BackToTopButton from "./Components/BackToTopButton/Index";
import EmailVerification from "./Components/EmailVerification";
import Footer from "./Components/Footer/Footer";
import NotFoundPage from "./Components/404ErrorPage/404ErrorPage";
import ResetPassword from "./Components/ChangePassword/ResetPassword";
import AdsManagement from "./Components/Dashboard/SuperAdminDashboard/AdsManagement/AdsManagement";
import CategoryManagement from "./Components/Dashboard/SuperAdminDashboard/CategoryManagement";
import UserManagement from "./Components/Dashboard/SuperAdminDashboard/UserManagement/UserManagement";
import { Box } from "@mui/material";
import About from "./Components/SinglePages/About";
import ContactUs from "./Components/SinglePages/ContactUs";
import PrivacyPolicy from "./Components/SinglePages/PrivacyPolicy";
import TermsOfService from "./Components/SinglePages/TermsOfService";
import Disclaimer from "./Components/SinglePages/Disclaimer";
import Favorites from "./Components/Dashboard/MyAds/Favorites/Favorites";
import MyOffers from "./Components/MyOffers/Index";
import ScrollToTop from "./Components/ScrollToTop";
import Inbox from "./Components/Dashboard/Inbox/Index";
import ChatPage from "./Components/Dashboard/Inbox/ChatPage";
import Notifications from "./Components/Dashboard/Notifications/Index";
import Offers from "./Components/Dashboard/Offers/Offers";
import AnalyticsTracker from "./Components/GASetup/AnalyticsTracker";
import SoldAds from "./Components/SoldAds/Index";
import WhatsNew from "./Components/Dashboard/WhatsNew";
import SearchesManagement from "./Components/Dashboard/SuperAdminDashboard/SearchesManagement/SearchesManagement";
import DeletedAdsManagement from "./Components/Dashboard/SuperAdminDashboard/AdsManagement/DeletedAdsManagement";
import ServicesPage from "./Components/ServicesSection/ServicesPage";
import SubServicesPage from "./Components/ServicesSection/SubServicesPage";
import SubServicesProviders from "./Components/ServicesSection/SubServicesProviders/Index";
import ProfilePage from "./Components/ProfilePage/Index";
import ServiceProviderProfile from "./Components/ServicesSection/ServiceProviderProfile/Index";
import ServicesManagement from "./Components/Dashboard/SuperAdminDashboard/ServicesManagement/ServicesManagement";
import ServiceProvidersManagement from "./Components/Dashboard/SuperAdminDashboard/ServiceProvidersManagement/ServiceProvidersManagement";
import MyServices from "./Components/Dashboard/MyServices/MyServices";
import NewNavbar from "./Components/Navbar/NewNavbar";
import GetStarted from "./Components/GetStarted/GetStarted";
import IDsManagement from "./Components/Dashboard/SuperAdminDashboard/IDsManagement";
import ContactFormEntriesManagement from "./Components/Dashboard/SuperAdminDashboard/ContactFormEntriesManagement";
import SubscribersManagement from "./Components/Dashboard/SuperAdminDashboard/SubscribersManagement";
import ImagesManagement from "./Components/Dashboard/SuperAdminDashboard/ImagesManagement/Index";
import AdsReports from "./Components/Dashboard/SuperAdminDashboard/AdsReportsManagement";
import AdminLiveChatMonitor from "./Components/Dashboard/SuperAdminDashboard/AdminLiveChatMonitor/Index";
import DatabaseBackup from "./Components/Dashboard/SuperAdminDashboard/DatabaseBackup/Index";

function App() {
  const location = useLocation();
  const hideHeaderFooter = location.pathname === "/get-started"; // Hide on GetStarted page

  return (
    <HelmetProvider>
      <div className="App">
        <AnalyticsTracker />
        <ScrollToTop />
        <Box
          sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
        >
          {/* Main Content */}
          <Box>
            <Outlet /> {/* This will render your page content */}
            {!hideHeaderFooter && <NewNavbar />}
            <div className="content">
              <Routes>
                <Route
                  path="/"
                  element={
                    <PublicRoute>
                      <Homepage />
                    </PublicRoute>
                  }
                />
                <Route path="/ads" element={<AdsPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route
                  path="/service/:serviceId"
                  element={<SubServicesPage />}
                />
                <Route
                  path="/service/:serviceId/:subServiceId"
                  element={<SubServicesProviders />}
                />
                <Route path="/profile/:id" element={<ProfilePage />} />
                <Route
                  path="/expert/:id"
                  element={<ServiceProviderProfile />}
                />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/ad/:uniqueId" element={<AdDetails />} />
                <Route path="/category/:id" element={<AdsPage />} />
                <Route path="/sold-items" element={<SoldAds />} />
                <Route
                  path="/reset-password/:token"
                  element={<ResetPassword />}
                />
                <Route
                  path="/get-started"
                  element={
                    <PublicRoute>
                      <GetStarted />
                    </PublicRoute>
                  }
                />
                {/* Email verification Route */}
                <Route
                  path="/verify-email/:token"
                  element={<EmailVerification />}
                />
                <Route
                  path="/chat/:chatId"
                  element={
                    <PrivateRoute>
                      <ChatPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                >
                  <Route
                    path="dashboard"
                    element={
                      <PrivateRoute>
                        <Dashboard />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="whats-new"
                    element={
                      <PrivateRoute>
                        <WhatsNew />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="notifications"
                    element={
                      <PrivateRoute>
                        <Notifications />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="inbox"
                    element={
                      <PrivateRoute>
                        <Inbox />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="offers"
                    element={
                      <PrivateRoute>
                        <Offers />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="my-ads"
                    element={
                      <PrivateRoute>
                        <MyAds />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="my-services"
                    element={
                      <PrivateRoute>
                        <MyServices />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="my-favorites"
                    element={
                      <PrivateRoute>
                        <Favorites />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="profile"
                    element={
                      <PrivateRoute>
                        <Profile />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/users-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <UserManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/categories-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <CategoryManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/ads-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <AdsManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/searches-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <SearchesManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/deleted-ads-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <DeletedAdsManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/services-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <ServicesManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/experts-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <ServiceProvidersManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/users-ids-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <IDsManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/images-management"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <ImagesManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/contact-form"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <ContactFormEntriesManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/subscribers"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <SubscribersManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/reports"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <AdsReports />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/live-chat"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <AdminLiveChatMonitor />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="admin/database-backup"
                    element={
                      <PrivateRoute requiredRole="superAdmin">
                        <DatabaseBackup />
                      </PrivateRoute>
                    }
                  />
                </Route>
                <Route
                  path="/post-ad"
                  element={
                    <PrivateRoute>
                      <PostAd />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/offers/:id"
                  element={
                    <PrivateRoute>
                      <MyOffers />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/edit-ad/:uniqueId"
                  element={
                    <PrivateRoute>
                      <EditAd />
                    </PrivateRoute>
                  }
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
          </Box>
        </Box>
        <BackToTopButton />
        {!hideHeaderFooter && <Footer />}
      </div>
    </HelmetProvider>
  );
}

export default App;
